/**
 * How wide the COP's right column opens, and where that choice is remembered.
 *
 * The bounds and the clamp used to live in `CopDockSheet`, which made every
 * other sheet import a component module to reach a pure function. They belong
 * here now for a second reason: the column FLOATS over the map, so its width is
 * no longer a fact about one sheet's box. It is the distance the map's own
 * right-edge chrome has to stand clear of, and the number `CopSidePanel`
 * publishes as `CopStageInsets.right`.
 *
 * No React and no DOM beyond `localStorage`, so the rules can be tested without
 * rendering a COP.
 */

/**
 * Narrowest the column may be, in CSS px.
 *
 * Inherited unchanged from when this lived in `CopDockSheet`. It was originally
 * set by the drone workspace's three segment tabs having to fit on one row -
 * which is why the third was abbreviated to "A/C" - and neither half of that
 * premise survives: T8 (#505) retired the abbreviation, and slice 11 retired the
 * third tab itself, so the segments read "Live / Camera" (`WORKSPACE_SEGMENT`)
 * with about 132 px each rather than 88. The number is kept because it is what
 * every sheet has been laid out against, not because it has been re-derived -
 * and it now has more slack than the day it was chosen.
 */
export const MIN_DOCK_SHEET_WIDTH = 288;

/** Widest, in CSS px. Past this the column stops being a panel beside the map. */
export const MAX_SHEET_WIDTH = 560;

/**
 * What the column opens at for an operator who has never dragged it.
 *
 * It used to be `window.innerWidth * 0.3` clamped to 560, recomputed on every
 * load - so on a 1920 px display it opened at 560 and an operator who dragged it
 * narrower got 560 back the next session. 320 leaves 32 px of headroom over the
 * enforced minimum, and on a 1920 px screen it is the single largest pixel item
 * in the declutter plan: 240 px of map returned on the first click of the golden
 * flow.
 *
 * It has NOT been checked in a browser. Slice 11 is what makes the details panel
 * comfortable at this width, and it has now landed: the fact grid is three tiles
 * rather than six, no reading in the panel is drawn twice, and the segment strip
 * is two tabs rather than three. The other three sheets are still laid out for
 * 560 and shown at 320.
 */
export const DEFAULT_SIDE_PANEL_WIDTH = 320;

/**
 * Clamp a candidate width to the bounds the resize handle enforces: never
 * narrower than `MIN_DOCK_SHEET_WIDTH`, never wider than `MAX_SHEET_WIDTH` or
 * half the viewport, whichever is smaller.
 */
export function clampDockSheetWidth(width: number, viewportWidth: number): number {
  const maxWidth = Math.min(MAX_SHEET_WIDTH, Math.floor(viewportWidth * 0.5));
  return Math.min(maxWidth, Math.max(MIN_DOCK_SHEET_WIDTH, width));
}

const STORAGE_PREFIX = 'cop_side_panel_width';

/**
 * Per operator, because a width is a working preference and a shared console is
 * a real deployment here. Keyed by whatever stable operator identity the caller
 * has (email today); with none, one shared anonymous slot. The same shape
 * `manual-layout.ts` uses, for the same reason.
 */
export function sidePanelWidthStorageKey(operator?: string | null): string {
  const who = operator?.trim();
  return who ? `${STORAGE_PREFIX}:${who}` : STORAGE_PREFIX;
}

/**
 * The operator's stored width, clamped to this viewport, or the default.
 *
 * Never throws. `localStorage` can throw outright (private mode, a locked-down
 * enterprise policy) and a stored value can be anything at all if another build
 * wrote it. Clamped on the way OUT as well as in, because a width stored on a
 * 2560 px display must not open past half of a 1366 px one.
 */
export function readSidePanelWidth(
  operator: string | null | undefined,
  viewportWidth: number,
): number {
  const fallback = clampDockSheetWidth(DEFAULT_SIDE_PANEL_WIDTH, viewportWidth);
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = Number(window.localStorage.getItem(sidePanelWidthStorageKey(operator)));
    return Number.isFinite(stored) && stored > 0
      ? clampDockSheetWidth(stored, viewportWidth)
      : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Returns false when the width could not be persisted.
 *
 * Nothing surfaces that yet, unlike `manualLayoutPersisted` and its siblings on
 * the manual strip, so on a browser that refuses storage the panel quietly opens
 * at the default every session. Stated rather than implied: the signal exists,
 * there is nowhere on the COP that says it.
 */
export function writeSidePanelWidth(width: number, operator?: string | null): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(sidePanelWidthStorageKey(operator), String(Math.round(width)));
    return true;
  } catch {
    return false;
  }
}
